import React, { useState, useEffect } from 'react';
import { Deployment } from '../../types/types';

interface VideoGalleryProps {
  isPreviewVideosRetrieved?: boolean;
  checkedDeployments: Record<string, boolean>;
  setCheckedDeployments: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  filteredDeployments: Deployment[];
}

const GalleryView: React.FC<VideoGalleryProps> = ({
  isPreviewVideosRetrieved,
  setCheckedDeployments,
  checkedDeployments,
  filteredDeployments,
}) => {
  const [selectedVideos, setSelectedVideos] = useState<Record<string, number>>(
    {}
  );

  useEffect(() => {
    if (filteredDeployments && filteredDeployments.length > 0) {
      setSelectedVideos((prev) => {
        const newState = { ...prev };
        filteredDeployments.forEach((deployment) => {
          if (!(deployment.date in newState)) {
            newState[deployment.date] = 0;
          }
        });
        return newState;
      });
    }
  }, [filteredDeployments]);

  const handleCheckboxChange = (deploymentDate: string) => {
    setCheckedDeployments((prev) => ({
      ...prev,
      [deploymentDate]: !prev[deploymentDate],
    }));
  };

  const handleVideoChange = (deploymentDate: string, index: number) => {
    setSelectedVideos((prevState) => ({
      ...prevState,
      [deploymentDate]: index,
    }));
  };

  if (!isPreviewVideosRetrieved) {
    return (
      <div className="flex items-center justify-center">
        <svg
          className="animate-spin h-8 w-8 text-gray-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
        <span className="ml-2 text-gray-600">Loading previews...</span>
      </div>
    );
  }

  if (
    (!filteredDeployments || filteredDeployments.length === 0) &&
    isPreviewVideosRetrieved
  ) {
    return <p className="text-center">No available preview</p>;
  }

  const allDeployments = filteredDeployments.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  const getYouTubeEmbedUrl = (url?: string) => {
    if (!url) return '';
    const videoIdMatch = url.match(/(?:v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    return videoIdMatch
      ? `https://www.youtube.com/embed/${videoIdMatch[1]}`
      : url;
  };

  return (
    <div className="pt-6">
      <div className="flex flex-wrap justify-center">
        {allDeployments.map((deployment) => {
          const trajectories = deployment.trajectories || [];
          const formattedTitle = deployment.label || deployment.date;

          if (trajectories.length === 0) {
            return (
              <div
                key={deployment.date}
                className="p-4 w-full sm:w-1/2 lg:w-1/3 xl:w-1/4"
              >
                <div className="bg-gray-200 dark:bg-gray-800 p-4 rounded shadow flex flex-col h-full justify-center items-center">
                  <div className="flex justify-center items-center mb-2">
                    <h2 className="text-xl font-semibold">{formattedTitle}</h2>
                    <input
                      type="checkbox"
                      className="ml-2"
                      style={{ accentColor: 'gray' }}
                      checked={!!checkedDeployments[deployment.date]}
                      onChange={() => handleCheckboxChange(deployment.date)}
                    />
                  </div>
                  <p className="text-gray-500 dark:text-gray-400">
                    No preview available
                  </p>
                </div>
              </div>
            );
          }

          const selectedVideoIndex = selectedVideos[deployment.date] || 0;
          const selectedVideo = trajectories[selectedVideoIndex];

          return (
            <div
              key={deployment.date}
              className="p-4 w-full sm:w-1/2 lg:w-1/3 xl:w-1/4"
            >
              <div className="bg-gray-200 dark:bg-gray-800 p-4 rounded shadow flex flex-col h-full">
                <div className="flex justify-center items-center mb-2">
                  <h2 className="text-xl font-semibold">{formattedTitle}</h2>
                  <input
                    type="checkbox"
                    className="ml-2"
                    style={{ accentColor: 'gray' }}
                    checked={!!checkedDeployments[deployment.date]}
                    onChange={() => handleCheckboxChange(deployment.date)}
                  />
                </div>
                <div className="flex-grow flex flex-col justify-center items-center">
                  {selectedVideo?.video ? (
                    <iframe
                      key={selectedVideo.video}
                      src={getYouTubeEmbedUrl(selectedVideo.video)}
                      className="w-full rounded mb-2"
                      style={{ maxHeight: '300px' }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">
                      No video available
                    </p>
                  )}
                </div>
                <h3 className="text-ml font-semibold mb-2 text-center">
                  Chosen preview:{' '}
                </h3>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  <div>
                    <span className="font-semibold">Trajectory:</span>
                    <span style={{ color: selectedVideo.color }}>
                      {' '}
                      {selectedVideo.color.charAt(0).toUpperCase() +
                        selectedVideo.color.slice(1)}
                    </span>
                  </div>
                </div>
                <div className="mt-4">
                  <h4 className="font-semibold mb-2">Available Previews:</h4>
                  <select
                    value={selectedVideoIndex}
                    onChange={(e) =>
                      handleVideoChange(
                        deployment.date,
                        parseInt(e.target.value)
                      )
                    }
                    className="w-full p-2 border rounded bg-white dark:bg-gray-700 text-black dark:text-white border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  >
                    {trajectories.map((traj, index) => (
                      <option
                        key={index}
                        value={index}
                        className="dark:bg-gray-800 dark:text-white"
                      >
                        {traj.color.charAt(0).toUpperCase() +
                          traj.color.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GalleryView;
